// The plan where a page meets it (02-DESIGN/17-plans/02-features-and-limits.md
// § the console; 02-DESIGN/03-console/01-the-console.md § Settings: the Plan
// tab): the workspace's plan read from identity.tenant.get by a form that
// checks beforehand, the link every refusal a plan causes carries to
// Settings → Plan, and the refusal itself naming the plan. A tab is a view
// of its page and never in the address, so the link opens Settings on the
// Plan tab through its navigation state, or switches the tab where Settings
// is already open.

import { createContext, useContext, type ReactNode } from "react";
import { Link } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { NotAllowed, Notice, refusedTitle, useLoad, type Load } from "../../ui/components.tsx";
import { Subjects, type Tenant, type TenantReply } from "../../wire/auth.ts";
import { SERVICE } from "../../wire/identity.ts";
import { useSurface } from "../surface.tsx";
import { holderOf, planRefusal } from "./plan.ts";
import { PLAN_TAB } from "./tabs.ts";

export { PLAN_TAB } from "./tabs.ts";

/** OpenPlan switches Settings to its Plan tab where Settings is the page already open. */
export const OpenPlan = createContext<(() => void) | undefined>(undefined);

/** PlanLink opens Settings → Plan. */
export function PlanLink({ children = "See the plan" }: { children?: ReactNode }) {
  const s = useSurface();
  const open = useContext(OpenPlan);
  if (open !== undefined) {
    return (
      <button type="button" className="text-link" onClick={open}>
        {children}
      </button>
    );
  }
  return (
    <Link className="text-link" to={tenantPath(s.tenant, "settings")} state={{ tab: PLAN_TAB }}>
      {children}
    </Link>
  );
}

/** Plan is the workspace's plan as a form reads it, and whose plan it is by name. */
export interface Plan {
  load: Load<Tenant>;
  tenant?: Tenant;
  /** Whose plan covers the workspace: its own name, or its root's. */
  holder: string;
}

/**
 * usePlan reads the workspace's plan for an admin's form; a member's form
 * reads nothing, since the verb answers an admin and refuses a member.
 */
export function usePlan(): Plan {
  const s = useSurface();
  const admin = s.offered("settings");
  const [load] = useLoad(`plan/${s.tenant}/${String(admin)}`, () =>
    admin
      ? s.bus.call<TenantReply>(SERVICE, Subjects.tenantGet(), undefined).then((r) => r.tenant)
      : Promise.reject(new Error("not read")),
  );
  const tenant = load.state === "ready" ? load.value : undefined;
  return { load, tenant, holder: tenant !== undefined ? holderOf(tenant, s.name).name : s.name };
}

/**
 * PlanRefused shows a refusal: one a plan caused names the plan and links to
 * Settings → Plan; any other is shown as it is.
 */
export function PlanRefused({
  err,
  what,
  holder,
  feature,
}: {
  err: unknown;
  what?: string;
  holder: string;
  /** The feature the form is about, where it knows. */
  feature?: string;
}) {
  const s = useSurface();
  const said = planRefusal(err, holder, s.name, feature);
  if (said === undefined) {
    return <NotAllowed err={err} what={what} />;
  }
  return (
    <Notice kind="bad" title={refusedTitle(err, what)}>
      {said} <PlanLink />
    </Notice>
  );
}
